import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import { useAudit } from "../hooks/useAudit";
import { useProfile } from "../utils/UseProfile";

const Audit = () => {
  const { profile } = useProfile();
  const {
    loading,
    profitData,
    investmentData,
    medicineData,
    calculateProfit,
    calculateTotalInvestment,
    calculateMedicineWiseInvestment,
  } = useAudit();

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [activeTab, setActiveTab] = useState("profit");
  const [searchMedicine, setSearchMedicine] = useState("");

  useEffect(() => {
    if (activeTab === "investment" && !investmentData) {
      calculateTotalInvestment();
    }
    if (activeTab === "medicine" && medicineData.content.length === 0) {
      calculateMedicineWiseInvestment(0, 15);
    }
  }, [activeTab]);

  const handleCalculateProfit = async () => {
    if (fromDate && toDate) await calculateProfit(fromDate, toDate);
  };

  const handleRefreshInvestment = async () => {
    await calculateTotalInvestment();
    await calculateMedicineWiseInvestment(0, 15);
  };

  const handleMedicinePage = (pageNo) => {
    calculateMedicineWiseInvestment(pageNo, 15);
  };

  const isAdmin = profile?.roleResponse?.role?.toLowerCase().includes("admin");

  const medicines = medicineData.content || [];
  const filteredMedicines = medicines.filter((item) =>
    item.name?.toLowerCase().includes(searchMedicine.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />

      <section className="relative mt-4 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">&#x1F4CA; Financial Audit</h2>
          <span className="text-xs text-gray-500"><i className="fas fa-clock mr-1"></i>{new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>

        <div className="flex-1 overflow-hidden px-4 sm:px-5 py-4 space-y-5 flex flex-col">

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1 inline-flex gap-1">
          {[
            { id: "profit", label: "Profit Analysis", icon: "fa-chart-line" },
            { id: "investment", label: "Investment", icon: "fa-coins" },
            { id: "medicine", label: "Product Breakdown", icon: "fa-pills" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <i className={`fas ${tab.icon} text-xs`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profit Analysis Tab */}
        {activeTab === "profit" && (
          <div className="space-y-5 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">From Date</label>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    className="w-44 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">To Date</label>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                    className="w-44 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                </div>
                <button onClick={handleCalculateProfit} disabled={loading}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-calculator" />}
                  {loading ? "Calculating..." : "Calculate Profit"}
                </button>
              </div>
            </div>

            {profitData ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Revenue</p>
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        ₹{profitData.totalRevenue?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}
                      </p>
                    </div>
                    <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center">
                      <i className="fas fa-arrow-trend-up text-green-500 text-lg"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Cost</p>
                      <p className="text-2xl font-bold text-orange-600 mt-1">
                        ₹{profitData.totalCost?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}
                      </p>
                    </div>
                    <div className="w-11 h-11 bg-orange-50 rounded-xl flex items-center justify-center">
                      <i className="fas fa-cart-shopping text-orange-500 text-lg"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net Profit</p>
                      <p className={`text-2xl font-bold mt-1 ${profitData.netProfit >= 0 ? "text-blue-600" : "text-red-600"}`}>
                        ₹{profitData.netProfit?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}
                      </p>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${
                        profitData.profitMargin >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>
                        <i className={`fas fa-arrow-${profitData.profitMargin >= 0 ? "up" : "down"} text-[10px]`}></i>
                        {Math.abs(profitData.profitMargin)?.toFixed(1)}% margin
                      </span>
                    </div>
                    <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                      <i className="fas fa-sack-dollar text-blue-500 text-lg"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Orders</p>
                      <p className="text-2xl font-bold text-indigo-600 mt-1">{profitData.totalOrders ?? 0}</p>
                      <p className="text-xs text-gray-400 mt-1">In selected range</p>
                    </div>
                    <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center">
                      <i className="fas fa-receipt text-indigo-500 text-lg"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total  Return</p>
                      <p className="text-2xl font-bold text-indigo-600 mt-1">{profitData.totalReturns ?? 0}</p>
                      <p className="text-xs text-gray-400 mt-1">In selected range</p>
                    </div>
                    <div className="w-11 h-11 bg-indigo-50 rounded-xl flex items-center justify-center">
                      <i className="fas fa-receipt text-indigo-500 text-lg"></i>
                    </div>
                  </div>
                </div>
              </div>
              
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-dashed border-gray-300 p-12 text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-chart-line text-blue-400 text-2xl"></i>
                </div>
                <p className="text-gray-500 font-medium">Select date range and calculate to view profit data</p>
                <p className="text-xs text-gray-400 mt-1">Revenue, cost, and margin will appear here</p>
              </div>
            )}
          </div>
        )}

        {/* Investment Tab */}
        {activeTab === "investment" && (
          <div className="space-y-5 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-800">Investment Overview</h3>
                <p className="text-xs text-gray-400 mt-0.5">Total capital invested in current stock inventory</p>
              </div>
              <button onClick={handleRefreshInvestment} disabled={loading}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium px-5 py-2 rounded-lg shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                {loading ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-sync-alt" />}
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>

            {loading && !investmentData ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
                <i className="fas fa-circle-notch fa-spin text-purple-500 text-3xl" />
                <p className="text-sm text-gray-400 mt-3">Loading investment data...</p>
              </div>
            ) : investmentData ? (() => {
              const totalInvested = investmentData.totalInvestment || 0;
              const currentInvested = investmentData.currentInvestment || 0;
              const totalProfit = investmentData.totalProfit || 0;
              const marketVal = investmentData.stockValue || 0;
              const potentialProfit = marketVal - currentInvested;
              const roi = currentInvested > 0 ? ((potentialProfit / currentInvested) * 100) : 0;
              const progressPct = currentInvested > 0 ? Math.min((marketVal / currentInvested) * 100, 200) : 0;
              return (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-rose-600 to-pink-700 rounded-xl shadow-lg p-5 text-white">
                      <p className="text-rose-200 text-xs font-medium uppercase tracking-wide">Total Investment</p>
                      <p className="text-2xl font-bold mt-1">₹{totalInvested.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                      <p className="text-rose-200 text-xs mt-1">All bills to distributors</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl shadow-lg p-5 text-white">
                      <p className="text-purple-200 text-xs font-medium uppercase tracking-wide">Current Investment</p>
                      <p className="text-2xl font-bold mt-1">₹{currentInvested.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                      <p className="text-purple-200 text-xs mt-1"><i className="fas fa-box mr-1" />{investmentData.totalItems || 0} items in stock</p>
                    </div>
                    <div className={`bg-gradient-to-br ${totalProfit >= 0 ? "from-green-600 to-emerald-700" : "from-red-600 to-rose-700"} rounded-xl shadow-lg p-5 text-white`}>
                      <p className="text-green-200 text-xs font-medium uppercase tracking-wide">Total Profit</p>
                      <p className="text-2xl font-bold mt-1">{totalProfit >= 0 ? "+" : ""}₹{totalProfit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                      <p className="text-green-200 text-xs mt-1">All-time net profit</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg p-5 text-white">
                      <p className="text-emerald-100 text-xs font-medium uppercase tracking-wide">Market Value</p>
                      <p className="text-2xl font-bold mt-1">₹{marketVal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                      <p className="text-emerald-100 text-xs mt-1">Current selling value</p>
                    </div>
                    <div className={`bg-gradient-to-br ${potentialProfit >= 0 ? "from-blue-500 to-cyan-600" : "from-red-500 to-rose-600"} rounded-xl shadow-lg p-5 text-white`}>
                      <p className="text-blue-100 text-xs font-medium uppercase tracking-wide">Potential Profit</p>
                      <p className="text-2xl font-bold mt-1">{potentialProfit >= 0 ? "+" : ""}₹{potentialProfit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                      <p className="text-blue-100 text-xs mt-1">If all stock is sold</p>
                    </div>
                    <div className={`bg-gradient-to-br ${roi >= 0 ? "from-amber-500 to-orange-600" : "from-red-500 to-rose-600"} rounded-xl shadow-lg p-5 text-white`}>
                      <p className="text-amber-100 text-xs font-medium uppercase tracking-wide">ROI</p>
                      <p className="text-2xl font-bold mt-1">{roi >= 0 ? "+" : ""}{roi.toFixed(1)}%</p>
                      <p className="text-amber-100 text-xs mt-1">Return on current stock</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700">Current Investment vs Market Value</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${roi >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {roi >= 0 ? "+" : ""}{roi.toFixed(1)}% ROI
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div className="absolute left-0 top-0 h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" style={{ width: "100%" }} />
                      <div className={`absolute left-0 top-0 h-full rounded-full transition-all duration-700 ${potentialProfit >= 0 ? "bg-gradient-to-r from-emerald-400 to-teal-400" : "bg-gradient-to-r from-red-400 to-rose-400"}`}
                        style={{ width: `${Math.min(progressPct, 100)}%` }} />
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-purple-600 font-medium">Current: ₹{currentInvested.toLocaleString("en-IN")}</span>
                      <span className={`text-xs font-medium ${potentialProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>Market: ₹{marketVal.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </>
              );
            })() : (
              <div className="bg-white rounded-xl shadow-sm border border-dashed border-gray-300 p-16 text-center">
                <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-coins text-purple-400 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">Investment data is loading...</p>
              </div>
            )}
          </div>
        )}

        {/* Product Breakdown Tab */}
        {activeTab === "medicine" && (
          <div className="space-y-4 animate-fadeIn flex flex-col min-h-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between flex-shrink-0">
              <div className="relative flex-1 max-w-sm">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input type="text" placeholder="Search product..." value={searchMedicine}
                  onChange={(e) => setSearchMedicine(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
              </div>
              <div className="flex items-center gap-3">
                {medicineData.totalElements > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 font-semibold px-3 py-1 rounded-full">
                    {medicineData.totalElements} total
                  </span>
                )}
                <button onClick={() => handleMedicinePage(0)} disabled={loading}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-5 py-2 rounded-lg shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                  {loading ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-sync-alt" />}
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
            </div>

            {/* Table with scroll */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1 min-h-0 max-h-[50vh]">
              <div className="overflow-auto flex-1">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase">#</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase">Product Name</th>
                      <th className="py-3 px-4 text-center text-xs font-semibold uppercase">Qty</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Purchase ₹</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Sell ₹</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Investment ₹</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Revenue ₹</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase">Profit ₹</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr><td colSpan="8" className="text-center py-12">
                        <i className="fas fa-circle-notch fa-spin text-blue-500 text-2xl" />
                        <p className="text-sm text-gray-400 mt-2">Loading...</p>
                      </td></tr>
                    ) : filteredMedicines.length === 0 ? (
                      <tr><td colSpan="8" className="text-center py-12">
                        <i className="fas fa-inbox text-gray-400 text-3xl mb-2 block" />
                        <p className="text-gray-500 font-medium">No data available</p>
                      </td></tr>
                    ) : (
                      filteredMedicines.map((item, index) => (
                        <tr key={index} className={`${index % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50 transition-colors`}>
                          <td className="py-2.5 px-4 text-sm text-gray-500">{medicineData.page * 15 + index + 1}</td>
                          <td className="py-2.5 px-4 text-sm font-medium text-gray-800"><span>{item.name}</span>{item.medicineType && <span className="text-xs text-gray-500 ml-1">| {item.medicineType}</span>}{item.manufacturer && <span className="text-xs text-gray-400 ml-1">| {item.manufacturer}</span>}</td>
                          <td className="py-2.5 px-4 text-center">
                            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">{item.quantity}</span>
                          </td>
                          <td className="py-2.5 px-4 text-right text-sm text-gray-700">₹{item.purchasePrice?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="py-2.5 px-4 text-right text-sm text-gray-700">₹{item.sellPrice?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="py-2.5 px-4 text-right text-sm font-semibold text-orange-600">₹{item.totalInvestment?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="py-2.5 px-4 text-right text-sm font-semibold text-blue-600">₹{item.potentialRevenue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="py-2.5 px-4 text-right">
                            <span className={`text-sm font-bold ${item.expectedProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {item.expectedProfit >= 0 ? "+" : ""}₹{item.expectedProfit?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {medicineData.totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
                  <span className="text-xs text-gray-500">
                    Page {medicineData.page + 1} of {medicineData.totalPages} ({medicineData.totalElements} items)
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleMedicinePage(0)} disabled={medicineData.page === 0}
                      className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
                    <button onClick={() => handleMedicinePage(medicineData.page - 1)} disabled={medicineData.page === 0}
                      className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
                    <button onClick={() => handleMedicinePage(medicineData.page + 1)} disabled={medicineData.page >= medicineData.totalPages - 1}
                      className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
                    <button onClick={() => handleMedicinePage(medicineData.totalPages - 1)} disabled={medicineData.page >= medicineData.totalPages - 1}
                      className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </section>

      <style>{`
        .animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default Audit;
